/* eslint-disable camelcase */
import type { AttributionGroupedTrend } from '@/api/attributionObservability'

type Group = AttributionGroupedTrend['groups'][number]
type Summary = Group['trend'][number]['summary']

export const OTHER_VERSIONS = '__other_versions__'
// Versions that together carry at most this share of meetings fold into one "Others" series.
const TAIL_SHARE = 0.1

const addSummaries = (a: Summary, b: Summary): Summary => {
  const sum = (x: number | undefined, y: number | undefined) => (x === undefined || y === undefined ? undefined : x + y)
  return {
    speaker_chip_count: sum(a.speaker_chip_count, b.speaker_chip_count),
    named_speaker_chip_count: sum(a.named_speaker_chip_count, b.named_speaker_chip_count),
    meeting_count: a.meeting_count + b.meeting_count,
    machine_labeled_speakers: a.machine_labeled_speakers + b.machine_labeled_speakers,
    active_speakers: a.active_speakers + b.active_speakers,
    ax_capture_ms: a.ax_capture_ms + b.ax_capture_ms,
    ax_readable_ms: a.ax_readable_ms + b.ax_readable_ms,
    speech_ms: a.speech_ms + b.speech_ms,
    ax_covered_speech_ms: a.ax_covered_speech_ms + b.ax_covered_speech_ms,
    words: a.words + b.words,
    machine_labeled_words: a.machine_labeled_words + b.machine_labeled_words,
    measured_label_meetings: sum(a.measured_label_meetings, b.measured_label_meetings),
    fully_machine_labeled_meetings: sum(a.fully_machine_labeled_meetings, b.fully_machine_labeled_meetings),
  }
}

export const foldVersionTail = (groups: Group[]): Group[] => {
  const versionOf = (group: Group) => group.path.find(({ dimension }) => dimension === 'app_version')?.value
  const meetings = new Map<string, number>()
  for (const group of groups) {
    const version = versionOf(group)
    if (version === undefined) return groups
    const count = group.trend.reduce((total, { summary }) => total + summary.meeting_count, 0)
    meetings.set(version, (meetings.get(version) ?? 0) + count)
  }
  const budget = Array.from(meetings.values()).reduce((total, count) => total + count, 0) * TAIL_SHARE
  const tail = new Set<string>()
  let folded = 0
  for (const [version, count] of Array.from(meetings).sort((a, b) => a[1] - b[1])) {
    if (folded + count > budget) break
    folded += count
    tail.add(version)
  }
  // Renaming a single version to "Others" would only hide which version it is.
  if (tail.size < 2) return groups
  const kept: Group[] = []
  const others = new Map<string, Group>()
  for (const group of groups) {
    if (!tail.has(versionOf(group)!)) {
      kept.push(group)
      continue
    }
    const path = group.path.map((step) =>
      step.dimension === 'app_version' ? { ...step, value: OTHER_VERSIONS } : step
    )
    const identity = JSON.stringify(path)
    const existing = others.get(identity)
    if (!existing) {
      others.set(identity, { path, trend: group.trend })
      continue
    }
    const byDate = new Map(existing.trend.map((point) => [point.date, point.summary]))
    for (const { date, summary } of group.trend) {
      const prior = byDate.get(date)
      byDate.set(date, prior ? addSummaries(prior, summary) : summary)
    }
    existing.trend = Array.from(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, summary]) => ({ date, summary }))
  }
  return [...kept, ...Array.from(others.values())]
}
